import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import GameContext from '../context/GameContext';
import { GameMoveModel } from '../types/GamesModel';
import { CorrectionOutcome } from './CorrectionBoard';
import {
  useCorrections,
  CorrectionMoveState,
  categoryOf,
  CATEGORY_PRIORITY,
} from './CorrectionsProvider';

// Drives "correct your mistakes" in place on the game overlay's own board. Owns which mistake is
// practised and whether practice is on; GamesListItemChessboard arms its Chessviewer and the panel
// below the board shows the prompt and result. Selecting a mistake also moves the game's currentMove
// there, so the move list and readout follow.
type PracticeContextType = {
  active: boolean;
  selected: CorrectionMoveState | null;
  // The mistake's move (where the review lands on exit) and the ply before it (the solve position).
  // prevMovePgn is null when the mistake is the game's first ply.
  selectedMove: GameMoveModel | null;
  prevMovePgn: string | null;
  outcome: CorrectionOutcome | undefined;
  allSolved: boolean;
  // Practice was entered for one move (mobile "Repair this move"), so solving it exits.
  singleMode: boolean;
  // Whether there's an earlier/later mistake for the prev/next repair nav.
  hasPrev: boolean;
  hasNext: boolean;
  // Whether any unsolved move remains across every tier: the reveal shows "Next" while true, "Done"
  // otherwise. advance() moves there, unlocking the next tier if needed.
  hasNextRepair: boolean;
  advance: () => void;
  start: () => void;
  stop: () => void;
  select: (moveId: number) => void;
  // Enter single-move repair for one mistake (by move id) and leave practice once it's solved.
  repairOne: (moveId: number) => void;
  next: () => void;
  // Step to the adjacent mistake in list order, solved or not. No-ops at the ends.
  goPrev: () => void;
  goNext: () => void;
  retry: () => void;
};

const PracticeContext = createContext<PracticeContextType>({
  active: false,
  selected: null,
  selectedMove: null,
  prevMovePgn: null,
  outcome: undefined,
  allSolved: false,
  singleMode: false,
  hasPrev: false,
  hasNext: false,
  hasNextRepair: false,
  advance: () => {},
  start: () => {},
  stop: () => {},
  select: () => {},
  repairOne: () => {},
  next: () => {},
  goPrev: () => {},
  goNext: () => {},
  retry: () => {},
});

export function usePractice(): PracticeContextType {
  return useContext(PracticeContext);
}

export default function PracticeProvider(props: { children: React.ReactNode }): React.ReactNode {
  const { game, setCurrentMove } = useContext(GameContext);
  // Practice cycles the selected categories (activeMistakes), but the progression reaches across every
  // tier (allMistakes) so finishing one unlocks the next.
  const {
    activeMistakes: mistakes,
    mistakes: allMistakes,
    enableCategory,
    outcomes,
    clearOutcome,
    solvedCount,
    totalCount,
    loading,
  } = useCorrections();

  const [active, setActive] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [singleMode, setSingleMode] = useState(false);

  // Flat move list and a MoveId -> index lookup, to find a mistake's move and the ply before it.
  const moves = useMemo(
    () => game.Moves.flatMap((m) => [m.White, m.Black]).filter(Boolean) as GameMoveModel[],
    [game.Moves],
  );

  const selected = useMemo(
    () => mistakes.find((m) => m.MoveId === selectedId) ?? null,
    [mistakes, selectedId],
  );

  const { selectedMove, prevMove, prevMovePgn } = useMemo(() => {
    if (selected == null) return { selectedMove: null, prevMove: null, prevMovePgn: null };
    const idx = moves.findIndex((m) => m.MoveId === selected.MoveId);
    if (idx < 0) return { selectedMove: null, prevMove: null, prevMovePgn: null };
    const prev = idx > 0 ? moves[idx - 1] : null;
    return { selectedMove: moves[idx], prevMove: prev, prevMovePgn: prev?.Pgn ?? null };
  }, [selected, moves]);

  const outcome = selected ? outcomes[selected.MoveId] : undefined;

  // The full repair sequence: worst tier first, move order within a tier.
  const repairOrder = useMemo(
    () =>
      [...allMistakes].sort(
        (a, b) =>
          CATEGORY_PRIORITY.indexOf(categoryOf(a.MoveType)) -
            CATEGORY_PRIORITY.indexOf(categoryOf(b.MoveType)) ||
          a.Number - b.Number ||
          a.Color - b.Color,
      ),
    [allMistakes],
  );

  // The next unsolved move in that order, skipping the given id. Null only when nothing is left.
  const nextToRepair = useCallback(
    (excludeId: number | null): CorrectionMoveState | null =>
      repairOrder.find((m) => !m.effectiveSolved && m.MoveId !== excludeId) ?? null,
    [repairOrder],
  );

  // Everything is repaired once there's nothing unsolved left across all tiers.
  const allSolved = repairOrder.length > 0 && repairOrder.every((m) => m.effectiveSolved);

  // Move practice to a mistake, unlocking its tier in the picker if it was off.
  const selectRepair = useCallback(
    (move: CorrectionMoveState) => {
      enableCategory(categoryOf(move.MoveType));
      setSelectedId(move.MoveId);
    },
    [enableCategory],
  );

  const start = useCallback(() => {
    setSingleMode(false);
    // Always start on the first move to repair, never a stale or solved one.
    const first = nextToRepair(null) ?? repairOrder[0] ?? null;
    if (first) selectRepair(first);
    setActive(true);
  }, [nextToRepair, repairOrder, selectRepair]);

  const stop = useCallback(() => {
    setActive(false);
    setSingleMode(false);
  }, []);

  const select = useCallback(
    (moveId: number) => {
      setSingleMode(false);
      // Unlock the move's tier if needed, so jumping to any mistake works.
      const m = allMistakes.find((x) => x.MoveId === moveId);
      if (m) enableCategory(categoryOf(m.MoveType));
      setSelectedId(moveId);
      setActive(true);
    },
    [allMistakes, enableCategory],
  );

  // Repair just this one move (mobile); the auto-exit effect below leaves practice once it's solved.
  const repairOne = useCallback(
    (moveId: number) => {
      setSingleMode(true);
      const m = allMistakes.find((x) => x.MoveId === moveId);
      if (m) enableCategory(categoryOf(m.MoveType));
      setSelectedId(moveId);
      setActive(true);
    },
    [allMistakes, enableCategory],
  );

  // The next move to repair after the current one: the reveal's "Next". Null only when everything is
  // solved, which is when "Done" shows.
  const nextRepair = nextToRepair(selected?.MoveId ?? null);
  const hasNextRepair = nextRepair != null;
  const advance = useCallback(() => {
    const nx = nextToRepair(selected?.MoveId ?? null);
    if (nx) selectRepair(nx);
  }, [nextToRepair, selected, selectRepair]);
  // next() is an alias for the same progression.
  const next = advance;

  // The header's prev/next repair chevrons, over the mistakes in play (activeOrder). Next also crosses
  // into the next tier's first unsolved move, so it's never greyed while something is left.
  const activeOrder = useMemo(
    () => repairOrder.filter((m) => mistakes.some((am) => am.MoveId === m.MoveId)),
    [repairOrder, mistakes],
  );
  const currentIndex = useMemo(
    () => (selected != null ? activeOrder.findIndex((m) => m.MoveId === selected.MoveId) : -1),
    [selected, activeOrder],
  );
  const hasPrev = currentIndex > 0;
  const hasNext =
    (currentIndex >= 0 && currentIndex < activeOrder.length - 1) || hasNextRepair;
  const goPrev = useCallback(() => {
    if (currentIndex > 0) setSelectedId(activeOrder[currentIndex - 1].MoveId);
  }, [currentIndex, activeOrder]);
  const goNext = useCallback(() => {
    if (currentIndex >= 0 && currentIndex < activeOrder.length - 1) {
      setSelectedId(activeOrder[currentIndex + 1].MoveId);
    } else {
      const nx = nextToRepair(selected?.MoveId ?? null);
      if (nx) selectRepair(nx);
    }
  }, [currentIndex, activeOrder, nextToRepair, selected, selectRepair]);

  const retry = useCallback(() => {
    if (selected != null) clearOutcome(selected.MoveId);
  }, [selected, clearOutcome]);

  // Single-move repair: leave practice once it's solved, after a beat so the success reveal lands.
  useEffect(() => {
    if (!active || !singleMode || !outcome?.correct) return;
    const t = setTimeout(() => {
      setActive(false);
      setSingleMode(false);
    }, 1200);
    return () => clearTimeout(t);
  }, [active, singleMode, outcome?.correct]);

  // While practising, select the move before the mistake (the one that produced the position on the
  // board) so the move list highlights it.
  useEffect(() => {
    if (active && selected != null) setCurrentMove(prevMove ?? undefined);
  }, [active, selected, prevMove, setCurrentMove]);

  // If the practised mistake disappears (e.g. the flip switched sides), leave practice. Ignore the brief
  // empty window while the new list loads.
  useEffect(() => {
    if (active && selectedId != null && !loading && !mistakes.some((m) => m.MoveId === selectedId)) {
      setActive(false);
      setSelectedId(null);
    }
  }, [active, selectedId, loading, mistakes]);

  const value = useMemo<PracticeContextType>(
    () => ({
      active,
      selected,
      selectedMove,
      prevMovePgn,
      outcome,
      allSolved,
      singleMode,
      hasPrev,
      hasNext,
      hasNextRepair,
      advance,
      start,
      stop,
      select,
      repairOne,
      next,
      goPrev,
      goNext,
      retry,
    }),
    [
      active,
      selected,
      selectedMove,
      prevMovePgn,
      outcome,
      allSolved,
      singleMode,
      hasPrev,
      hasNext,
      hasNextRepair,
      advance,
      start,
      stop,
      select,
      repairOne,
      next,
      goPrev,
      goNext,
      retry,
    ],
  );

  return <PracticeContext.Provider value={value}>{props.children}</PracticeContext.Provider>;
}
